if (!customElements.get('featured-collection-swiper')) {
  const DEFAULT_AUTOPLAY_SPEED = 6000;
  const MANUAL_SPEED = 300;

  // Whether the visitor is moving through the page with Tab. Script focus, such as the
  // quick add drawer handing focus back to its button on close, is not navigation, so
  // any pointer press clears the flag.
  let keyboardNavigation = false;
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Tab') keyboardNavigation = true;
  }, true);
  document.addEventListener('pointerdown', () => {
    keyboardNavigation = false;
  }, true);

  class FeaturedCollectionSwiper extends HTMLElement {
    constructor() {
      super();
      this.autoplayStoppedByUser = false;
      this.autoplayPausedByHover = false;
      this.pausedTranslate = null;
      this.pausedRemaining = 0;
      this.handleInteraction = this.handleInteraction.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleAutoplayAdvance = this.handleAutoplayAdvance.bind(this);
      this.scheduleAutoplayRestart = this.scheduleAutoplayRestart.bind(this);
    }

    connectedCallback() {
      this.waitForSwiper();
    }

    disconnectedCallback() {
      this.cancelSwiperLoad?.();
      clearTimeout(this.resizeTimer);
      clearTimeout(this.resumeFallbackTimer);
      window.removeEventListener('on:debounced-resize', this.handleResize);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('focus', this.handleFocus, true);
      this.removeEventListener('focusout', this.scheduleAutoplayRestart);
      document.removeEventListener('pointerup', this.scheduleAutoplayRestart);
      document.removeEventListener('pointercancel', this.scheduleAutoplayRestart);
      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.destroySwiper();
      this.initialized = false;
    }

    waitForSwiper() {
      this.cancelSwiperLoad?.();
      this.cancelSwiperLoad = theme.prepareSwiper(this, () => this.setup());
    }

    setup() {
      if (this.initialized) return;

      this.initialized = true;
      this.container = this.querySelector('.products-grid-container');
      this.wrapper = this.querySelector('.slider__grid');
      this.slides = [...this.querySelectorAll('.slider__item')];
      this.nextButton = this.querySelector('button[name="next"]');
      this.prevButton = this.querySelector('button[name="prev"]');
      this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || DEFAULT_AUTOPLAY_SPEED;

      this.addEventListener('pointerdown', this.handlePointerDown);
      // Capture, so this runs before Swiper's own focus listener on the slider.
      this.addEventListener('focus', this.handleFocus, true);
      this.addEventListener('focusout', this.scheduleAutoplayRestart);
      document.addEventListener('pointerup', this.scheduleAutoplayRestart);
      document.addEventListener('pointercancel', this.scheduleAutoplayRestart);
      this.addEventListener('pointerenter', this.handlePointerEnter);
      this.addEventListener('pointerleave', this.handlePointerLeave);
      window.addEventListener('on:debounced-resize', this.handleResize);

      this.update();
    }

    getSlidesPerView() {
      if (window.innerWidth >= 1280) return Number(this.dataset.slidesXl);
      if (window.innerWidth >= 1024) return Number(this.dataset.slidesLg);
      if (window.innerWidth >= 769) return Number(this.dataset.slidesMd);
      if (window.innerWidth >= 600) return Number(this.dataset.slidesSm);
      return Number(this.dataset.slidesMobile);
    }

    shouldEnable() {
      if (!this.container || !this.wrapper || this.slides.length < 2) return false;
      if (this.hasAttribute('disable-desktop') && window.innerWidth >= 600) return false;
      return this.slides.length > this.getSlidesPerView();
    }

    update() {
      if (!this.shouldEnable()) {
        this.destroySwiper();
        return;
      }

      if (!this.swiper) this.initSwiper();
    }

    initSwiper() {
      const slideGap = parseFloat(this.dataset.slideGap);
      const spaceBetween = Number.isFinite(slideGap)
        ? Math.max(0, slideGap)
        : parseFloat(getComputedStyle(this.wrapper).columnGap) || 0;
      const autoplay = this.dataset.autoplay === 'true' && !this.autoplayStoppedByUser
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.container.classList.add('swiper');
      this.wrapper.classList.add('swiper-wrapper');
      this.wrapper.style.columnGap = '0px';
      this.slides.forEach((slide) => slide.classList.add('swiper-slide'));

      // Auto-scroll runs as a continuous marquee (zero delay, one long transition
      // per card), so the wrapper has to ease linearly. Swiper's default `ease`
      // makes every card accelerate and then coast to a stop.
      if (autoplay) this.setLinearTiming(true);

      this.swiper = new window.Swiper(this.container, {
        autoplay: autoplay ? {
          delay: 0,
          disableOnInteraction: true,
          pauseOnMouseEnter: false,
          waitForTransition: true
        } : false,
        breakpoints: {
          600: { slidesPerView: Number(this.dataset.slidesSm) },
          769: { slidesPerView: Number(this.dataset.slidesMd) },
          1024: { slidesPerView: Number(this.dataset.slidesLg) },
          1280: { slidesPerView: Number(this.dataset.slidesXl) }
        },
        grabCursor: true,
        // The section already rebuilds on viewport resize. Swiper's observer can
        // resume a running transition during startup and double its travel distance.
        resizeObserver: false,
        updateOnWindowResize: false,
        loop: true,
        loopPreventsSliding: false,
        navigation: this.nextButton && this.prevButton ? {
          nextEl: this.nextButton,
          prevEl: this.prevButton
        } : false,
        on: {
          autoplay: this.handleAutoplayAdvance,
          sliderFirstMove: this.handleInteraction,
          touchEnd: this.scheduleAutoplayRestart
        },
        slidesPerView: Number(this.dataset.slidesMobile),
        spaceBetween,
        speed: autoplay ? this.autoplaySpeed : MANUAL_SPEED,
        watchOverflow: true
      });

      this.removeAttribute('inactive');
      // `:hover` sticks to the last tapped element on touch screens, so only trust it
      // where the primary input can hover.
      if (this.matches(':hover') && window.matchMedia('(hover: hover)').matches) this.handlePointerEnter();
      if (keyboardNavigation && this.contains(document.activeElement)) this.handleFocus();
    }

    destroySwiper() {
      clearTimeout(this.resumeFallbackTimer);
      clearTimeout(this.interactionTimer);

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.wrapper) {
        this.wrapper.classList.remove('swiper-wrapper');
        this.wrapper.style.removeProperty('column-gap');
      }
      this.container?.classList.remove('swiper');
      this.setLinearTiming(false);
      this.slides?.forEach((slide) => slide.classList.remove('swiper-slide'));
      this.autoplayPausedByHover = false;
      this.autoplayStoppedByUser = false;
      this.pausedTranslate = null;
      this.setAttribute('inactive', '');
    }

    setLinearTiming(enabled) {
      if (!this.container) return;

      if (enabled) {
        this.container.style.setProperty('--swiper-wrapper-transition-timing-function', 'linear');
      } else {
        this.container.style.removeProperty('--swiper-wrapper-transition-timing-function');
      }
    }

    /** Distance in px that one autoplay step covers, i.e. one card plus its gap. */
    getSlideStep() {
      const { slidesGrid, slidesSizesGrid, activeIndex, params } = this.swiper;

      if (slidesGrid?.length > 1) {
        const index = Math.min(Math.max(activeIndex, 1), slidesGrid.length - 1);
        const step = Math.abs(slidesGrid[index] - slidesGrid[index - 1]);
        if (step > 0) return step;
      }

      return (slidesSizesGrid?.[0] || 0) + (params.spaceBetween || 0);
    }

    /**
     * Halts the in-flight transition where it currently sits, and reports its target
     * plus the distance still to run. Duration is deliberately derived from that
     * distance rather than from elapsed time, so repeated hovers over one card each
     * resume at the same velocity instead of compounding into a crawl.
     */
    freezeTransition() {
      const { swiper } = this;
      const target = swiper.translate;
      const current = swiper.getTranslate();

      swiper.setTransition(0);
      swiper.setTranslate(current);

      return { target, remaining: Math.abs(target - current) };
    }

    /** Milliseconds the marquee needs to cover `distance` at its normal speed. */
    getDurationFor(distance) {
      const step = this.getSlideStep();
      return step > 0 ? Math.round((distance / step) * this.autoplaySpeed) : 0;
    }

    handleInteraction() {
      clearTimeout(this.interactionTimer);
      if (this.autoplayStoppedByUser || !this.swiper || this.dataset.autoplay !== 'true'
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.autoplayStoppedByUser = true;
      this.pausedTranslate = null;
      clearTimeout(this.resumeFallbackTimer);
      this.swiper.autoplay.stop();
      this.freezeTransition();
      // Autoplay borrows `speed` as the per-card glide duration. Once the visitor has
      // taken over, arrows and drags must not inherit that multi-second easing.
      this.swiper.params.speed = MANUAL_SPEED;
      this.setLinearTiming(false);
      // Re-anchor to the card nearest the frozen position so the next manual
      // navigation lands on the grid rather than part-way between two cards.
      this.swiper.updateActiveIndex();
      this.swiper.updateSlidesClasses();
    }

    scheduleAutoplayRestart() {
      clearTimeout(this.interactionTimer);
      if (!this.autoplayStoppedByUser) return;

      this.interactionTimer = setTimeout(() => {
        const { swiper } = this;
        if (!swiper || this.autoplayPausedByHover || swiper.touchEventsData.isTouched
          || (keyboardNavigation && this.contains(document.activeElement))) return;

        this.autoplayStoppedByUser = false;
        swiper.params.speed = this.autoplaySpeed;
        this.setLinearTiming(true);
        swiper.autoplay.start();
      }, MANUAL_SPEED + 150);
    }

    /**
     * Arrow presses stop auto-scroll here rather than on `click`, because Swiper reads
     * `speed` when the arrow's click handler runs and that must already be MANUAL_SPEED.
     * @param {PointerEvent} evt - Pointer event object.
     */
    handlePointerDown(evt) {
      if (evt.target.closest('.slider-nav__btn')) this.handleInteraction();
    }

    /**
     * Swiper's `a11y.scrollOnFocus` slides any focused card to the front. That is
     * right for Tab, but when script returns focus to a card that has moved on, such
     * as after the quick add drawer closes, the carousel snaps to it. Swiper reads the
     * option per event, so it is set here for each focus before Swiper handles it.
     */
    handleFocus() {
      if (!this.swiper) return;

      this.swiper.params.a11y.scrollOnFocus = keyboardNavigation;
      if (keyboardNavigation) this.handleInteraction();
    }

    /**
     * Pauses auto-scroll while a cursor is over the carousel. Touch is ignored: a tap
     * has no matching leave until the visitor taps somewhere else, so it would hold the
     * carousel paused long after they have moved on.
     * @param {PointerEvent} [evt] - Omitted when init finds the cursor already over it.
     */
    handlePointerEnter(evt) {
      if (evt?.pointerType === 'touch') return;
      if (this.autoplayStoppedByUser) {
        this.autoplayPausedByHover = true;
        return;
      }
      if (this.autoplayStoppedByUser || this.autoplayPausedByHover || !this.swiper?.autoplay?.running) return;

      clearTimeout(this.resumeFallbackTimer);
      this.autoplayPausedByHover = true;
      this.swiper.autoplay.pause();

      this.capturePausedState();
    }

    capturePausedState() {
      const { target, remaining } = this.freezeTransition();
      this.pausedTranslate = target;
      this.pausedRemaining = remaining;
    }

    /**
     * A zero-delay autoplay schedules its next advance with requestAnimationFrame, and
     * `autoplay.pause()` only clears its timeout - it cannot cancel that frame. So an
     * advance queued just before the visitor hovered still fires, and the card creeps
     * on under a fresh full-length transition. Swiper emits `autoplay` synchronously
     * with that advance, before the browser has painted it, so re-freezing here catches
     * it at the same pixel and re-points the resume at the new target.
     */
    handleAutoplayAdvance() {
      if (!this.autoplayPausedByHover || this.autoplayStoppedByUser || !this.swiper) return;

      this.capturePausedState();
    }

    /** @param {PointerEvent} evt - Pointer event object. */
    handlePointerLeave(evt) {
      if (evt.pointerType === 'touch') return;
      if (this.autoplayStoppedByUser) {
        this.autoplayPausedByHover = false;
        this.scheduleAutoplayRestart();
        return;
      }
      if (!this.autoplayPausedByHover || this.autoplayStoppedByUser || !this.swiper) return;

      const { swiper } = this;
      const duration = this.getDurationFor(this.pausedRemaining);

      this.autoplayPausedByHover = false;

      // `autoplay.resume()` starts a fresh full-length transition to the next card,
      // so the part-card that is left over would crawl across at a fraction of the
      // normal speed. Instead, finish the interrupted card over what remains of its
      // own duration. Swiper's `waitForTransition` handler then restarts autoplay
      // from the resulting card boundary, back at full speed.
      if (this.pausedTranslate === null || duration < 16) {
        this.resumeAutoplay();
        return;
      }

      swiper.setTransition(duration);
      swiper.setTranslate(this.pausedTranslate);
      this.watchResumedTransition(duration);
    }

    /** Resuming an autoplay that is not actually paused queues a second advance. */
    resumeAutoplay() {
      const autoplay = this.swiper?.autoplay;
      if (autoplay?.running && autoplay.paused) autoplay.resume();
    }

    /** Restarts autoplay by hand if the resumed transition never fires transitionend. */
    watchResumedTransition(duration) {
      const { wrapperEl } = this.swiper;

      const onTransitionEnd = (event) => {
        if (event.target !== wrapperEl) return;
        wrapperEl.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(this.resumeFallbackTimer);
      };

      wrapperEl.addEventListener('transitionend', onTransitionEnd);
      clearTimeout(this.resumeFallbackTimer);
      this.resumeFallbackTimer = setTimeout(() => {
        wrapperEl.removeEventListener('transitionend', onTransitionEnd);
        if (this.autoplayStoppedByUser || this.autoplayPausedByHover) return;
        this.resumeAutoplay();
      }, duration + 150);
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.destroySwiper();
        this.update();
      }, 100);
    }
  }

  customElements.define('featured-collection-swiper', FeaturedCollectionSwiper);
}
